'use client';

/* eslint-disable no-underscore-dangle */
import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { useAtom } from 'jotai';
import avo from 'analytics/avo';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardStaticPlaceholder from 'components/consumer/ProductCardStaticPlaceholder';
import useProductCardDataNext from 'hooks/useProductCardDataNext';
import { createSearchProductClickHandler } from 'components/consumer/consumer-search/algolia-insights-helpers/createSearchProductClickHandler';
import lastInsightsClickAtom from 'components/consumer/consumer-search/advanced-search/atoms/lastInsightsClickAtom';
import { getVariantsByMatchedColorFirst } from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits/utils';
const PLACEHOLDER_COUNT = 3;

/**
 * Search Modal Product Grid component
 * Uses GraphQL data for full product details with images and prices
 * Makes one batch GraphQL call for all products
 */
const SearchModalProductGridNext = () => {
  const $ = _c(28);
  const {
    hits
  } = useHits();
  const {
    status
  } = useInstantSearch();
  const [, setLastInsightsClick] = useAtom(lastInsightsClickAtom);
  let t0;
  if ($[0] !== hits) {
    t0 = hits.slice(0, 3);
    $[0] = hits;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const limitedHits = t0;
  const isSearching = status === "loading" || status === "stalled";
  let t1;
  if ($[2] !== limitedHits) {
    t1 = limitedHits.map(_temp);
    $[2] = limitedHits;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const productIds = t1;
  let t2;
  if ($[4] !== productIds) {
    t2 = {
      productIds
    };
    $[4] = productIds;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    products,
    loading
  } = useProductCardDataNext(t2);
  let t3;
  if ($[6] !== hits || $[7] !== products) {
    let t4;
    if ($[9] !== hits) {
      t4 = product => ({
        ...product,
        variantsToShow: getVariantsByMatchedColorFirst(product, hits)
      });
      $[9] = hits;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = products.map(t4);
    $[6] = hits;
    $[7] = products;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const productsWithHighlightedVariantsFirst = t3;
  let t4;
  if ($[11] !== productsWithHighlightedVariantsFirst) {
    t4 = Object.fromEntries((productsWithHighlightedVariantsFirst ?? []).map(_temp2));
    $[11] = productsWithHighlightedVariantsFirst;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const productMap = t4;
  if (!limitedHits || limitedHits.length === 0) {
    if (isSearching) {
      let t5;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <ProductCardGridUI columnsOnDesktop="3" columnsOnMobile="2" limitWidth={false}>{Array.from({
            length: PLACEHOLDER_COUNT
          }, _temp3)}</ProductCardGridUI>;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      return t5;
    }
    let t5;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="text-center py-8 text-gray-500" role="status" aria-live="polite">No products found</div>;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  if (loading) {
    let t5;
    if ($[15] !== limitedHits) {
      t5 = limitedHits.map(_temp4);
      $[15] = limitedHits;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== t5) {
      t6 = <ProductCardGridUI columnsOnDesktop="3" columnsOnMobile="2" limitWidth={false}>{t5}</ProductCardGridUI>;
      $[17] = t5;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  let t5;
  if ($[19] !== limitedHits || $[20] !== productMap || $[21] !== setLastInsightsClick) {
    let t6;
    if ($[23] !== productMap || $[24] !== setLastInsightsClick) {
      t6 = (hit_1, index_0) => {
        const productId = parseInt(hit_1.product_id || hit_1.objectID, 10);
        const product_1 = {
          ...productMap[productId],
          name: hit_1?.name ?? productMap[productId]?.name ?? ""
        };
        if (!product_1 || !product_1.id) {
          return <ProductCardStaticPlaceholder key={hit_1.objectID} />;
        }
        const algoliaMetaData = {
          objectID: hit_1.objectID ?? String(productId),
          queryID: hit_1.__queryID ?? "",
          position: index_0 + 1
        };
        return <ProductCardStatic key={hit_1.objectID} {...product_1} position={index_0 + 1} layout="minimal" analyticsComponentName={avo.Component.PRODUCT_CARD} onClick={createSearchProductClickHandler(algoliaMetaData, setLastInsightsClick, {
          hit: hit_1,
          productId
        })} />;
      };
      $[23] = productMap;
      $[24] = setLastInsightsClick;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    t5 = limitedHits.map(t6);
    $[19] = limitedHits;
    $[20] = productMap;
    $[21] = setLastInsightsClick;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[26] !== t5) {
    t6 = <ProductCardGridUI columnsOnDesktop="3" columnsOnMobile="2" limitWidth={false}>{t5}</ProductCardGridUI>;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
};
export default SearchModalProductGridNext;
function _temp(hit) {
  return parseInt(hit.product_id || hit.objectID, 10);
}
function _temp2(product_0) {
  return [product_0.id, product_0];
}
function _temp3(_, index) {
  return <ProductCardStaticPlaceholder key={`search-placeholder-${index}`} />;
}
function _temp4(hit_0) {
  return <ProductCardStaticPlaceholder key={hit_0.objectID} />;
}